import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import track from 'react-tracking';
import { reduxForm, formValues, Field as ReduxFormField } from 'redux-form';
import ReactMarkdown from 'markdown-to-jsx';
import { v4 as uuidv4 } from 'uuid';

import Field from '@ynap/field';
import Checkbox from '@ynap/checkbox';
import BEMHelper from '@ynap/bem-helper';
import Notification from '@ynap/notification';
import { SimpleAsyncButton, Button } from '@ynap/button';
import { RadioButtonGroup, RadioButton } from '@ynap/radio-button';
import { authActions, getAutomaticMarketingConsent } from '@ynap/auth-state';
import { submitAndCollateErrorsPromise, onSubmitFail } from '@ynap/form-utils';

import validate from './validate';
import collateErrors from './collateErrors';
import LoginFormSection from '../LoginFormSection/LoginFormSection';
import { emailInfoOverlay } from '../RegistrationForm/RegistrationForm';
import RegistrationFormSection from '../RegistrationFormSection/RegistrationFormSection';
import { trackForgotPassword, trackCheckoutLoginOptions, trackReadPrivacyPolicy, trackReadAboutEmail, trackMarketingConsent } from '../analytics';

const bem = new BEMHelper('LoginOrGuestForm');

const { selectMarketingConsentIfNeeded } = authActions;
@track()
@connect(getAutomaticMarketingConsent, { selectMarketingConsentIfNeeded })
export class Consent extends PureComponent {
    static propTypes = {
        form: PropTypes.string.isRequired,
        field: PropTypes.string.isRequired,
        locale: PropTypes.shape({
            country: PropTypes.string.isRequired,
            id: PropTypes.string.isRequired,
        }).isRequired,
        className: PropTypes.string,
        handleOpen: PropTypes.func,
        handleToggleConsent: PropTypes.func,
        selectMarketingConsentIfNeeded: PropTypes.func,
        messages: {
            loginOrGuestForm: PropTypes.shape({
                maMarketingConsent: PropTypes.func.isRequired,
                emailTitle: PropTypes.func.isRequired,
            }).isRequired,
        },
        checkoutAnalytics: PropTypes.object,
        discount15OffCountries: PropTypes.array,
        maMarketingConsent: PropTypes.string,
    };

    constructor() {
        super();
        this.handleEmailClick = this.handleEmailClick.bind(this);
        this.handleCheckboxClick = this.handleCheckboxClick.bind(this);
        this.state = {
            maMarketingConsent: false,
        };
    }

    componentDidMount() {
        this.setState({ maMarketingConsent: this.props.maMarketingConsent === 'true' });
        const { form, field, selectMarketingConsentIfNeeded } = this.props;
        selectMarketingConsentIfNeeded && selectMarketingConsentIfNeeded(form, field);
    }

    @track(trackReadAboutEmail)
    handleEmailClick() {
        this.props.handleOpen?.();
    }

    @track(({ checkoutAnalytics }, state, [event]) => {
        const consentValue = event.currentTarget.checked;
        return trackMarketingConsent(consentValue, checkoutAnalytics);
    })
    handleCheckboxClick(e) {
        this.setState({ maMarketingConsent: e.currentTarget.checked.toString() === 'true' });
        this.props.handleToggleConsent?.(e.currentTarget.checked.toString());
    }

    render() {
        const { messages, className = '', discount15OffCountries, locale, enableAgeConsent, enableTcConsent, enableGenderSelection } = this.props;
        const { country } = locale;
        const multipleConsents = enableAgeConsent || enableTcConsent;

        return (
            <div className={className}>
                <Field
                    className={bem('marketing')}
                    name="maMarketingConsent"
                    component={Checkbox}
                    format={(value) => value === 'true'}
                    normalize={(value) => value.toString()}
                    onChange={this.handleCheckboxClick}
                    onBlur={(e) => e.preventDefault()}
                >
                    <label className={bem('marketingConsentText')} htmlFor="maMarketingConsent">
                        {messages.loginOrGuestForm?.maMarketingConsent({ is15OffCountry: discount15OffCountries?.includes(country) })}
                        {messages.registrationForm.discountDisclaimer && (<ReactMarkdown
                            options={{
                                overrides: {
                                    a: {
                                        component: Button,
                                        props: {
                                            className: bem('tandcLink'),
                                            type: 'link',
                                            component: 'a',
                                            target: '_blank',
                                            rel: 'noopener',
                                        },
                                    },
                                },
                            }}
                        >
                            {messages.registrationForm.discountDisclaimer({ locale: locale.id })}
                        </ReactMarkdown>)}
                    </label>
                </Field>
                <Button type="link" component="a" target="_blank" rel="noopener" onClick={this.handleEmailClick} className={bem('emailLink')}>
                    {messages.loginOrGuestForm.emailTitle()}
                </Button>
                {this.state.maMarketingConsent && enableGenderSelection && (
                    <Field
                        name="gender"
                        component={RadioButtonGroup}
                        onBlur={(e) => e.preventDefault()}
                        label={messages.registrationForm.genderSelectionLabel()}
                        className={bem('gender', multipleConsents && 'withCheckbox')}
                    >
                        <RadioButton value="Female" label={messages.registrationForm.genderWomens()} showError className={bem('genderOption')} />
                        <RadioButton value="Male" label={messages.registrationForm.genderMens()} showError className={bem('genderOption')} />
                        <RadioButton value="Unspecified" label={messages.registrationForm.genderAll()} showError className={bem('genderOption')} />
                    </Field>
                )}
            </div>
        );
    }
}

@track()
export class LoginOrGuestForm extends PureComponent {
    static propTypes = {
        form: PropTypes.string,
        login: PropTypes.func,
        createFastUser: PropTypes.func,
        handleSubmit: PropTypes.func,
        onSubmit: PropTypes.func,
        mergeCart: PropTypes.bool,
        submitting: PropTypes.bool,
        error: PropTypes.string,
        formType: PropTypes.string,
        className: PropTypes.string,
        links: PropTypes.shape({
            termsAndConditions: PropTypes.string.isRequired,
            privacyPolicy: PropTypes.string.isRequired,
        }).isRequired,
        messages: PropTypes.shape({
            loginOrGuestForm: PropTypes.shape({
                signInInfoMessage: PropTypes.func.isRequired,
                hasAccountTitle: PropTypes.func.isRequired,
                hasAccountDescription: PropTypes.func.isRequired,
                forgotPasswordLinkTitle: PropTypes.func.isRequired,
                noAccountTitle: PropTypes.func.isRequired,
                noAccountDescription: PropTypes.func.isRequired,
                logonId: PropTypes.func.isRequired,
                submit: PropTypes.func.isRequired,
                maMarketingConsent: PropTypes.func.isRequired,
                emailTitle: PropTypes.func.isRequired,
            }).isRequired,
            registrationForm: PropTypes.shape({
                maMarketingConsent: PropTypes.func.isRequired,
                privacyPolicy: PropTypes.func.isRequired,
                privacyPolicyTitle: PropTypes.func.isRequired,
            }).isRequired,
            loginForm: PropTypes.shape({
                logonId: PropTypes.func.isRequired,
                logonPassword: PropTypes.func.isRequired,
            }).isRequired,
            errors: PropTypes.shape({
                required: PropTypes.func.isRequired,
                invalid: PropTypes.func.isRequired,
                authenticationError: PropTypes.func.isRequired,
                loginNotAllowed: PropTypes.func.isRequired,
                global: PropTypes.func.isRequired,
            }).isRequired,
        }).isRequired,
        handleToggleConsent: PropTypes.func.isRequired,
        checkoutAnalytics: PropTypes.object,
        register: PropTypes.func,
        affiliate: PropTypes.object,
        locale: { country: PropTypes.string, language: PropTypes.string, id: PropTypes.string },
        multipleLanguageCountries: PropTypes.array,
        config: PropTypes.object,
        gdprConsent: PropTypes.bool,
        ageConsent: PropTypes.bool,
        tcConsent: PropTypes.bool,
        initialValues: PropTypes.object,
    };

    constructor(props) {
        super(props);
        const { formType } = props.initialValues || {};
        this.state = {
            showOverlay: false,
            formType,
            maMarketingConsent: false,
            preferredLanguage: null,
            tmSessionId: null,
        };

        this.toggleOverlay = this.toggleOverlay.bind(this);
        this.proceedForgotPassword = this.proceedForgotPassword.bind(this);
        this.handlePrivacyPolicyClick = this.handlePrivacyPolicyClick.bind(this);
        this.changeFormType = this.changeFormType.bind(this);
        this.handleToggleMktConsent = this.handleToggleMktConsent.bind(this);
    }

    componentDidMount() {
        const { guestFastTrackEmail, initialValues, config } = this.props;
        const { orgId, url: tmUrl } = config?.threatMetrix || {};

        if (!!config?.threatMetrix && window?.tm?.prfl) {
            const tmSessionId = uuidv4();

            this.setState({ tmSessionId });
            window.tm.prfl(tmUrl, orgId, tmSessionId);
        }

        const maMarketingConsent = initialValues?.registration?.maMarketingConsent;
        this.setState({ maMarketingConsent });

        guestFastTrackEmail && this.fastTrackGuestUser(guestFastTrackEmail);
    }

    fastTrackGuestUser(guestEmail) {
        const { handleSubmit } = this.props;
        handleSubmit(() => this.submit({ guestEmail, maMarketingConsent: 'false', hasAccount: 'false' }))();
    }

    @track(trackForgotPassword)
    proceedForgotPassword() {}

    @track(trackReadPrivacyPolicy)
    handlePrivacyPolicyClick() {}

    getSubmitAction(values) {
        const {
            login,
            register,
            affiliate,
            locale: { country, language, id: localeId },
            multipleLanguageCountries,
            form,
            mergeCart,
            createFastUser,
            config,
        } = this.props;
        const { logonId } = values;
        const tmEnabled = !!config?.threatMetrix;

        switch (values.formType) {
            case 'login':
                return login(
                    { logonId, ...values.login, mergeCart, tmEnabled, tmSessionId: this.state.tmSessionId },
                    undefined,
                    undefined,
                    `/${localeId}/checkout/shipping-address`,
                );
            case 'registration': {
                const { registrationEmail } = values;
                const { maMarketingConsent, ...rest } = values.registration; // eslint-disable-line no-unused-vars
                const { maMarketingConsent: maMarketingConsentFromState } = this.state;
                const updatedRegistration = {
                    ...rest,
                    ...(maMarketingConsentFromState && { maMarketingConsent: maMarketingConsentFromState }),
                };
                const fallbackLangISO =
                    multipleLanguageCountries && multipleLanguageCountries.hasOwnProperty(country)
                        ? multipleLanguageCountries[country].filter((langISO) => langISO.includes(language))[0]
                        : language;
                const selectedPreferredLanguage = (maMarketingConsentFromState === 'true' && this.state.preferredLanguage) || fallbackLangISO;

                return register(
                    {
                        logonId: registrationEmail,
                        affiliate,
                        ...updatedRegistration,
                        preferredLanguage: selectedPreferredLanguage,
                        mergeCart,
                        tmEnabled,
                        tmSessionId: this.state.tmSessionId,
                    },
                    form,
                    `/${localeId}/checkout/shipping-address`,
                );
            }
            default: {
                const { guestEmail, maMarketingConsent, gender } = values;
                return createFastUser({ logonId: guestEmail, maMarketingConsent, gender });
            }
        }
    }

    submit = (values) => {
        const action = this.getSubmitAction(values);
        return submitAndCollateErrorsPromise(action, values, this.props);
    };

    toggleOverlay() {
        this.setState({ showOverlay: !this.state.showOverlay });
    }

    @track(({ checkoutAnalytics }, state, [formType]) => trackCheckoutLoginOptions(formType, checkoutAnalytics))
    changeFormType(formType) {
        const { parentForm, change } = this.props;
        const formPrefix = parentForm ? `${parentForm}.` : '';

        this.setState({ formType });
        change(`${formPrefix}formType`, formType);
    }

    handleToggleMktConsent(maMarketingConsent, preferredLanguage) {
        this.setState({ ...(maMarketingConsent && { maMarketingConsent }), ...(preferredLanguage && { preferredLanguage }) });
    }

    render() {
        const {
            messages,
            className,
            form,
            handleSubmit,
            submitting,
            error,
            locale,
            links,
            handleToggleConsent,
            checkoutAnalytics,
            config,
            multipleLanguageCountries,
            gdprConsent,
            ageConsent,
            tcConsent,
            _reduxForm,
        } = this.props;
        const { formType } = this.state;
        const { titles, flags, discount15OffCountries } = config;
        const { country } = locale;
        const { enableTitles = true, enableGenderSelection } = flags;
        const getFormModifier = (form) => (form === formType ? 'checked' : 'unchecked');
        return (
            <form id={form} method="post" className={bem(null, null, className)} onSubmit={handleSubmit(this.submit)}>
                {messages.loginOrGuestForm.signInInfoMessage && <div className={bem('infoMessage')}>{messages.loginOrGuestForm.signInInfoMessage()}</div>}

                {error ? (
                    <Notification
                        type="error"
                        className={bem('error')}
                        message={
                            <ReactMarkdown
                                options={{
                                    overrides: {
                                        a: (props) => (
                                            <a
                                                className={bem('errorLink')}
                                                href={locale ? `/${locale.id}${props.href}` : props.href}
                                                rel="noopener noreferrer"
                                            >
                                                {props.children}
                                            </a>
                                        )
                                    },
                                }}
                            >
                                {error}
                            </ReactMarkdown>
                        }
                    />
                ) : null}

                <div className={bem('formsContainer')}>
                    <div onClick={() => this.changeFormType('login')} className={bem('formOption', getFormModifier('login'))} id="loginOrGuestForm_login">
                        <div className={bem('formOptionHeader', getFormModifier('login'))}>
                            <input type="radio" />
                            <div>
                                <h2 className={bem('optionLabel')}>{messages.loginOrGuestForm.hasAccountTitle()}</h2>
                                <span>{messages.loginOrGuestForm.hasAccountDescription()}</span>
                            </div>
                        </div>
                        {formType === 'login' && (
                            <div className={bem('extraFields', getFormModifier('login'))}>
                                <Field
                                    className={bem('logonId')}
                                    name="logonId"
                                    type="email"
                                    label={messages.loginOrGuestForm.logonId()}
                                    autoComplete="username"
                                />
                                <LoginFormSection className={bem('loginForm')} name="login" messages={messages} hideLogonId isCheckoutLayout={true} />
                                <a href={`/${locale.id}/account/forgot-password`} className={bem('forgotPasswordLink')} onClick={this.proceedForgotPassword}>
                                    {messages.loginOrGuestForm.forgotPasswordLinkTitle()}
                                </a>
                                <SimpleAsyncButton
                                    className={bem('submit')}
                                    containerClass={bem('buttonWrapper')}
                                    loading={submitting}
                                    copy={messages.loginOrGuestForm.submit({ formType: 'login' })}
                                />
                            </div>
                        )}
                    </div>
                    {
                        <div
                            onClick={() => this.changeFormType('registration')}
                            className={bem('formOption', getFormModifier('registration'))}
                            id="loginOrGuestForm_register"
                        >
                            <div className={bem('formOptionHeader', getFormModifier('registration'))}>
                                <input type="radio" />
                                <div>
                                    <h2 className={bem('optionLabel')}>{messages.loginOrGuestForm.createAccountTitle()}</h2>
                                    <span>
                                        {messages.loginOrGuestForm?.createAccountDescription({ is15OffCountry: discount15OffCountries?.includes(country) })}
                                    </span>
                                </div>
                            </div>

                            {formType === 'registration' && (
                                <div className={bem('extraFields', getFormModifier('registration'))}>
                                    <Field
                                        className={bem('logonId')}
                                        name="registrationEmail"
                                        type="email"
                                        label={messages.loginOrGuestForm.logonId()}
                                        autoComplete="username"
                                    />
                                    <RegistrationFormSection
                                        className={bem('registrationForm')}
                                        name="registration"
                                        form={form}
                                        messages={messages}
                                        titles={titles}
                                        links={links}
                                        hideLogonId
                                        enableTitles={enableTitles}
                                        locale={locale}
                                        gdprConsent={gdprConsent}
                                        enableAgeConsent={ageConsent}
                                        enableTcConsent={tcConsent}
                                        maMarketingConsent={this.state.maMarketingConsent}
                                        preferredLanguage={this.state.preferredLanguage}
                                        handleToggleMktConsent={this.handleToggleMktConsent}
                                        supportedLanguages={multipleLanguageCountries?.[locale?.country]}
                                        changeField={this.props.change}
                                        enableGenderSelection={enableGenderSelection}
                                        config={config}
                                        field={'registration.maMarketingConsent'}
                                    />
                                    <SimpleAsyncButton
                                        className={bem('submit')}
                                        containerClass={bem('buttonWrapper')}
                                        loading={submitting}
                                        copy={messages.loginOrGuestForm.submit({ formType: 'registration' })}
                                    />
                                </div>
                            )}
                        </div>
                    }
                    {formType === 'guest' ? (
                        <div onClick={() => this.changeFormType('guest')} className={bem('formOption', getFormModifier('guest'))} id="loginOrGuestForm_guest">
                            <div className={bem('formOptionHeader', getFormModifier('guest'))}>
                                <input type="radio" />
                                <div>
                                    <h2 className={bem('optionLabel')}>{messages.loginOrGuestForm.noAccountTitle()}</h2>
                                    <span>{messages.loginOrGuestForm.noAccountDescription()}</span>
                                </div>
                            </div>
                            {formType === 'guest' && (
                                <div className={bem('extraFields', getFormModifier('guest'))}>
                                    <Field
                                        className={bem('logonId')}
                                        name="guestEmail"
                                        type="email"
                                        label={messages.loginOrGuestForm.logonId()}
                                        autoComplete="username"
                                    />
                                    {messages.registrationForm.maMarketingConsent ? (
                                        <Consent
                                            className={bem('marketingConsent')}
                                            form={form}
                                            field="maMarketingConsent"
                                            messages={messages}
                                            links={links}
                                            handleOpen={this.toggleOverlay}
                                            handleToggleConsent={handleToggleConsent}
                                            locale={locale}
                                            checkoutAnalytics={checkoutAnalytics}
                                            isCheckoutLayout={true}
                                            discount15OffCountries={discount15OffCountries}
                                            enableAgeConsent={ageConsent}
                                            enableTcConsent={tcConsent}
                                            enableGenderSelection={enableGenderSelection}
                                            maMarketingConsent={_reduxForm?.values?.maMarketingConsent}
                                        />
                                    ) : null}
                                    <SimpleAsyncButton
                                        className={bem('submit')}
                                        containerClass={bem('buttonWrapper')}
                                        loading={submitting}
                                        copy={messages.loginOrGuestForm.submit({ formType: 'guest' })}
                                    />
                                    <p className={bem('terms')}>
                                        {messages.registrationForm.privacyPolicy()}
                                        <Button
                                            type="link"
                                            component="a"
                                            href={links.privacyPolicy}
                                            target="_blank"
                                            rel="noopener"
                                            onClick={this.handlePrivacyPolicyClick}
                                            className={bem('privacyPolicyLink')}
                                        >
                                            {messages.registrationForm.privacyPolicyTitle()}
                                        </Button>
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <ReactMarkdown
                            className={bem('optionDesc')}
                            options={{
                                overrides: {
                                    a: (props) => (
                                        <a
                                            onClick={() => this.changeFormType('guest')}
                                            className={bem('guestLink')}
                                            id="loginOrGuestForm_guest"
                                            rel="noopener noreferrer"
                                        >
                                            {props.children}
                                        </a>
                                    ),
                                },
                            }}
                        >
                            {messages.loginOrGuestForm?.continueAsGuest()}
                        </ReactMarkdown>
                    )}
                </div>
                {this.state.showOverlay && emailInfoOverlay(messages, locale, this.toggleOverlay)}
            </form>
        );
    }
}

const initialValues = {
    formType: null,
};

export const LoginOrGuestFormWithValues = formValues({ formType: 'formType' })(LoginOrGuestForm);

export default reduxForm({ form: 'loginOrGuest', initialValues, validate: validate(), collateErrors, onSubmitFail })(LoginOrGuestFormWithValues);
